import { isAppleSubscriptionRunning } from '@/lib/billing/apple-subscription';
import { isNativeShell } from '@/lib/native/platform';
import { type SessionProfile, useSessionStore } from '@/lib/stores/session.store';

/**
 * Whether this build may show a way to buy something through Stripe:
 * licences, seats, credit, storage, add-ons, referral rewards.
 *
 * Two things rule it out.
 *
 * The native shells. Licences and credit are sold on the web, and the native
 * shells reach that checkout through the very same SPA. Apple does not allow
 * that: apps relying on the "other purchase methods" exemptions (App Review
 * Guidelines 3.1.3) "cannot, within the app, encourage users to use a
 * purchasing method other than in-app purchase". That covers buttons, links
 * and any wording that asks the user to buy — not just a checkout screen.
 * Three rejections in August 2026 (KAN-10) were about exactly this. So inside
 * the native shell every Stripe entry point is gone: the checkout redirects,
 * and the remaining banners keep their status text but lose their call to
 * action. Stating a fact ("trial ends in 5 days") is fine; asking for the sale
 * is not. Nothing here may point at the website either — naming it is itself a
 * call to action in Apple's reading. Google Play is more relaxed, but the same
 * rule is applied to both shells: one behaviour is easier to keep correct than
 * two, and it removes the same risk from the Play review. What the iOS app
 * sells itself goes through the App Store (lib/billing/app-store-access.ts).
 * Super-admins are the exception, see {@link usesWebBilling}.
 *
 * A running App Store subscription, on every platform. Such a workspace is a
 * single licence paid to Apple; the server refuses every Stripe purchase while
 * it runs, so offering one would only end in that refusal.
 *
 * Reads the profile at call time. The surfaces asking this render from the
 * profile anyway, so they re-render when it changes.
 */
export function canShowPurchaseFlow(): boolean {
  const { profile } = useSessionStore.getState();
  return usesWebBilling(profile) && !isAppleSubscriptionRunning(profile);
}

/**
 * Whether this session gets the web's Stripe billing: always on the web, and
 * inside the native shells for super-admins only (KAN-1895). They look into
 * customer workspaces from their phone and need the same billing view there.
 * App Review never signs in as a super-admin, so what it sees is unchanged.
 */
export function usesWebBilling(profile: SessionProfile | null): boolean {
  return !isNativeShell() || profile?.isSuperadmin === true;
}
